import { supabase } from './supabase.js';

// Cache for static shopping data (categories and store layouts)
const STATIC_CACHE_VERSION = '1.0';
const STATIC_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

const getStaticDataCache = () => {
  try {
    const cached = localStorage.getItem(`shopping-static-${STATIC_CACHE_VERSION}`);
    const timestamp = localStorage.getItem(`shopping-static-timestamp-${STATIC_CACHE_VERSION}`);
    
    if (cached && timestamp) {
      const cacheAge = Date.now() - parseInt(timestamp);
      if (cacheAge < STATIC_CACHE_DURATION) {
        return JSON.parse(cached);
      }
    }
  } catch (error) {
    console.error('Error reading shopping static cache:', error);
  }
  return null;
};

const setStaticDataCache = (data) => {
  try {
    localStorage.setItem(`shopping-static-${STATIC_CACHE_VERSION}`, JSON.stringify(data));
    localStorage.setItem(`shopping-static-timestamp-${STATIC_CACHE_VERSION}`, Date.now().toString());
  } catch (error) {
    console.error('Error caching shopping static data:', error);
  }
};

// Auto-categorization rules for common items
const ITEM_CATEGORIES = {
  // Produce
  produce: ['apple', 'banana', 'orange', 'lettuce', 'tomato', 'potato', 'onion', 'carrot', 'broccoli', 'spinach', 'cucumber', 'bell pepper', 'strawberry', 'grape', 'watermelon', 'cantaloupe', 'pineapple', 'mango', 'avocado', 'corn', 'celery', 'mushroom', 'garlic', 'ginger', 'lemon', 'lime', 'peach', 'pear', 'plum', 'cherry', 'azureberry', 'raspberry', 'blackberry', 'kiwi', 'asparagus', 'zucchini', 'squash', 'emerald bean', 'peas', 'cauliflower', 'cabbage', 'brussels sprout', 'kale', 'arugula', 'basil', 'cilantro', 'parsley', 'mint', 'rosemary', 'thyme', 'sage'],
  
  // Dairy & Eggs
  dairy: ['milk', 'cheese', 'yogurt', 'butter', 'cream', 'sour cream', 'cottage cheese', 'cream cheese', 'egg', 'half and half', 'whipping cream', 'buttermilk', 'mozzarella', 'cheddar', 'parmesan', 'swiss', 'feta', 'goat cheese', 'ricotta', 'azure cheese', 'ice cream', 'frozen yogurt'],
  
  // Meat & Seafood
  meat: ['chicken', 'beef', 'pork', 'turkey', 'ham', 'bacon', 'sausage', 'ground beef', 'steak', 'roast', 'ribs', 'lamb', 'veal', 'duck', 'salmon', 'tuna', 'shrimp', 'fish', 'crab', 'lobster', 'scallop', 'tilapia', 'cod', 'halibut', 'mahi', 'swordfish', 'catfish', 'trout', 'bass', 'hot dog', 'bratwurst', 'pepperoni', 'salami', 'prosciutto', 'deli meat', 'lunch meat'],
  
  // Bakery
  bakery: ['bread', 'bagel', 'muffin', 'croissant', 'donut', 'cake', 'cookie', 'pie', 'pastry', 'roll', 'bun', 'tortilla', 'pita', 'naan', 'focaccia', 'baguette', 'sourdough', 'rye', 'wheat', 'neutral-50 bread', 'english muffin', 'cracker', 'pretzel'],
  
  // Frozen
  frozen: ['frozen pizza', 'ice cream', 'frozen vegetable', 'frozen fruit', 'frozen meal', 'frozen dinner', 'popsicle', 'frozen waffle', 'frozen pancake', 'frozen fries', 'tater tots', 'frozen chicken', 'frozen fish', 'frozen shrimp', 'sorbet', 'gelato', 'frozen yogurt', 'ice', 'frozen juice'],
  
  // Pantry
  pantry: ['rice', 'pasta', 'bean', 'lentil', 'flour', 'sugar', 'salt', 'pepper', 'oil', 'vinegar', 'sauce', 'cereal', 'oatmeal', 'granola', 'peanut butter', 'jelly', 'jam', 'honey', 'syrup', 'ketchup', 'mustard', 'mayo', 'salad dressing', 'soup', 'broth', 'stock', 'tomato sauce', 'tomato paste', 'canned tomato', 'canned vegetable', 'canned fruit', 'canned bean', 'tuna can', 'chicken can', 'crackers', 'chips', 'popcorn', 'nuts', 'dried fruit', 'chocolate', 'candy', 'coffee', 'tea', 'cocoa', 'baking soda', 'baking powder', 'vanilla', 'spice', 'herb', 'quinoa', 'couscous', 'barley', 'bulgur'],
  
  // Beverages
  beverages: ['water', 'juice', 'soda', 'coffee', 'tea', 'milk', 'almond milk', 'soy milk', 'oat milk', 'coconut milk', 'sports drink', 'energy drink', 'beer', 'wine', 'liquor', 'sparkling water', 'lemonade', 'iced tea', 'hot chocolate', 'smoothie', 'kombucha', 'coconut water'],
  
  // Household
  household: ['paper towel', 'toilet paper', 'tissue', 'napkin', 'cleaning', 'detergent', 'soap', 'shampoo', 'conditioner', 'toothpaste', 'toothbrush', 'deodorant', 'razor', 'shaving cream', 'lotion', 'sunscreen', 'battery', 'light bulb', 'trash bag', 'aluminum foil', 'plastic wrap', 'parchment paper', 'zip lock', 'storage bag', 'sponge', 'dish soap', 'laundry', 'bleach', 'fabric softener', 'dryer sheet', 'air freshener', 'candle'],
  
  // Personal Care
  personal: ['shampoo', 'conditioner', 'body wash', 'soap', 'face wash', 'moisturizer', 'lotion', 'sunscreen', 'toothpaste', 'toothbrush', 'floss', 'mouthwash', 'deodorant', 'razor', 'shaving cream', 'makeup', 'nail polish', 'cotton', 'q-tip', 'band-aid', 'medicine', 'vitamin', 'supplement', 'contact solution', 'glasses cleaner', 'hair gel', 'hair spray', 'perfume', 'cologne'],
  
  // Baby
  baby: ['diaper', 'wipes', 'formula', 'baby food', 'baby cereal', 'pacifier', 'bottle', 'sippy cup', 'bib', 'baby shampoo', 'baby lotion', 'baby powder', 'baby oil', 'teething', 'baby medicine'],
  
  // Pet Supplies
  pet: ['dog food', 'cat food', 'pet food', 'dog treat', 'cat treat', 'pet treat', 'cat litter', 'pet toy', 'pet bed', 'pet bowl', 'leash', 'collar', 'pet shampoo', 'flea', 'tick', 'pet medicine']
};

// Store layout configurations
const STORE_LAYOUTS = {
  'harris_teeter': {
    name: 'Harris Teeter',
    layout: [
      { category: 'produce', aisle: '1-3', order: 1 },
      { category: 'bakery', aisle: '4', order: 2 },
      { category: 'deli', aisle: '5', order: 3 },
      { category: 'meat', aisle: '6-7', order: 4 },
      { category: 'seafood', aisle: '8', order: 5 },
      { category: 'dairy', aisle: '9-10', order: 6 },
      { category: 'frozen', aisle: '11-14', order: 7 },
      { category: 'pantry', aisle: '15-20', order: 8 },
      { category: 'snacks', aisle: '21-22', order: 9 },
      { category: 'beverages', aisle: '23-24', order: 10 },
      { category: 'household', aisle: '25-26', order: 11 },
      { category: 'personal', aisle: '27', order: 12 },
      { category: 'baby', aisle: '28', order: 13 },
      { category: 'pet', aisle: '29', order: 14 }
    ]
  },
  'target': {
    name: 'Target',
    layout: [
      { category: 'personal', aisle: 'A1-A10', order: 1 },
      { category: 'baby', aisle: 'B1-B5', order: 2 },
      { category: 'household', aisle: 'C1-C8', order: 3 },
      { category: 'pet', aisle: 'D1-D3', order: 4 },
      { category: 'pantry', aisle: 'F1-F15', order: 5 },
      { category: 'snacks', aisle: 'G1-G5', order: 6 },
      { category: 'beverages', aisle: 'G6-G8', order: 7 },
      { category: 'frozen', aisle: 'H1-H6', order: 8 },
      { category: 'dairy', aisle: 'I1-I3', order: 9 },
      { category: 'meat', aisle: 'J1-J2', order: 10 },
      { category: 'produce', aisle: 'K1-K4', order: 11 },
      { category: 'bakery', aisle: 'L1', order: 12 }
    ]
  },
  'walmart': {
    name: 'Walmart',
    layout: [
      { category: 'produce', aisle: '1-4', order: 1 },
      { category: 'bakery', aisle: '5', order: 2 },
      { category: 'meat', aisle: '6-8', order: 3 },
      { category: 'dairy', aisle: '9-11', order: 4 },
      { category: 'frozen', aisle: '12-16', order: 5 },
      { category: 'pantry', aisle: '17-25', order: 6 },
      { category: 'snacks', aisle: '26-28', order: 7 },
      { category: 'beverages', aisle: '29-31', order: 8 },
      { category: 'baby', aisle: '32-33', order: 9 },
      { category: 'pet', aisle: '34-35', order: 10 },
      { category: 'household', aisle: '36-40', order: 11 },
      { category: 'personal', aisle: '41-45', order: 12 }
    ]
  },
  'generic': {
    name: 'Generic Store',
    layout: [
      { category: 'produce', aisle: 'Entrance', order: 1 },
      { category: 'bakery', aisle: 'Left Wall', order: 2 },
      { category: 'deli', aisle: 'Left Wall', order: 3 },
      { category: 'meat', aisle: 'Back Wall', order: 4 },
      { category: 'seafood', aisle: 'Back Wall', order: 5 },
      { category: 'dairy', aisle: 'Right Wall', order: 6 },
      { category: 'frozen', aisle: 'Center Aisles', order: 7 },
      { category: 'pantry', aisle: 'Center Aisles', order: 8 },
      { category: 'snacks', aisle: 'Center Aisles', order: 9 },
      { category: 'beverages', aisle: 'Center Aisles', order: 10 },
      { category: 'household', aisle: 'Center Aisles', order: 11 },
      { category: 'personal', aisle: 'Pharmacy Area', order: 12 },
      { category: 'baby', aisle: 'Pharmacy Area', order: 13 },
      { category: 'pet', aisle: 'Center Aisles', order: 14 }
    ]
  }
};

export const shoppingService = {
  // Auto-categorize an item based on its name (cached)
  categorizeItem(itemName) {
    const lowerName = itemName.toLowerCase();
    
    // Check cache first
    const cached = getStaticDataCache();
    const categories = cached?.itemCategories || ITEM_CATEGORIES;
    
    // Cache static data if not already cached
    if (!cached) {
      const staticData = {
        itemCategories: ITEM_CATEGORIES,
        storeLayouts: STORE_LAYOUTS
      };
      setStaticDataCache(staticData);
    }
    
    for (const [categoryId, keywords] of Object.entries(categories)) {
      if (keywords.some(keyword => lowerName.includes(keyword))) {
        return categoryId;
      }
    }
    
    return 'other'; // Default category
  },

  // Get category name from ID
  getCategoryName(categoryId) {
    const categoryNames = {
      produce: 'Produce',
      dairy: 'Dairy & Eggs',
      meat: 'Meat & Seafood',
      bakery: 'Bakery',
      frozen: 'Frozen',
      pantry: 'Pantry',
      snacks: 'Snacks',
      beverages: 'Beverages',
      household: 'Household',
      personal: 'Personal Care',
      baby: 'Baby',
      pet: 'Pet Supplies',
      other: 'Other'
    };
    
    return categoryNames[categoryId] || 'Other';
  },

  // Shopping Categories operations
  async getCategories() {
    const { data, error } = await supabase
      .from('shopping_categories')
      .select('*')
      .eq('is_active', true)
      .order('sort_order', { ascending: true });
    
    if (error) throw error;
    return data;
  },

  async createCategory(category) {
    const { data, error } = await supabase
      .from('shopping_categories')
      .insert([{
        name: category.name,
        emoji: category.emoji,
        color: category.color,
        sort_order: category.sortOrder || 999,
        preferred_store: category.preferredStore,
        store_aisle: category.storeAisle
      }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async updateCategory(categoryId, updates) {
    const { data, error } = await supabase
      .from('shopping_categories')
      .update({
        name: updates.name,
        emoji: updates.emoji,
        color: updates.color,
        sort_order: updates.sortOrder,
        preferred_store: updates.preferredStore,
        store_aisle: updates.storeAisle,
        is_active: updates.isActive
      })
      .eq('id', categoryId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Shopping Items operations
  async getItems(filters = {}) {
    let query = supabase
      .from('shopping_items')
      .select(`
        *,
        category:shopping_categories(id, name, emoji, color),
        requester:family_members!requested_by(id, name, avatar),
        assignee:family_members!assigned_to(id, name, avatar),
        purchaser:family_members!purchased_by(id, name, avatar)
      `);
    
    // Apply filters
    if (filters.status) {
      query = query.eq('status', filters.status);
    }
    if (filters.priority) {
      query = query.eq('priority', filters.priority);
    }
    if (filters.categoryId) {
      query = query.eq('category_id', filters.categoryId);
    }
    if (filters.assignedTo) {
      query = query.eq('assigned_to', filters.assignedTo);
    }
    if (filters.store) {
      query = query.eq('store', filters.store);
    }
    
    // Sort by priority and category
    query = query
      .order('priority', { ascending: false })
      .order('category_id', { ascending: true })
      .order('created_at', { ascending: false });
    
    const { data, error } = await query;
    
    if (error) throw error;
    return data;
  },

  async addItem(item, requesterId) {
    console.log('➕ Adding shopping item:', { item, requesterId });
    
    // Get requester details
    const { data: requester, error: requesterError } = await supabase
      .from('family_members')
      .select('name')
      .eq('id', requesterId)
      .single();
    
    if (requesterError) throw requesterError;
    
    // Auto-categorize if no category provided
    let categoryId = item.categoryId;
    if (!categoryId) {
      const categoryName = this.categorizeItem(item.name);
      const { data: category } = await supabase
        .from('shopping_categories')
        .select('id')
        .ilike('name', `%${this.getCategoryName(categoryName)}%`)
        .single();
      
      categoryId = category?.id;
    }
    
    const { data, error } = await supabase
      .from('shopping_items')
      .insert([{
        name: item.name,
        quantity: item.quantity || 1,
        unit: item.unit,
        notes: item.notes,
        brand_preference: item.brandPreference,
        category_id: categoryId,
        store: item.store,
        aisle: item.aisle,
        priority: item.priority || 'normal',
        status: 'pending',
        requested_by: requesterId,
        requested_by_name: requester.name,
        assigned_to: item.assignedTo,
        is_recurring: item.isRecurring || false,
        recurrence_pattern: item.recurrencePattern,
        source_type: item.sourceType || 'manual',
        source_id: item.sourceId,
        tags: item.tags || [],
        image_url: item.imageUrl
      }])
      .select(`
        *,
        category:shopping_categories(id, name, emoji, color),
        requester:family_members!requested_by(id, name, avatar)
      `)
      .single();
    
    if (error) throw error;
    
    console.log('✅ Shopping item added:', data);
    return data;
  },

  async updateItem(itemId, updates) {
    const updateData = {};
    
    // Map updates to database columns
    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.quantity !== undefined) updateData.quantity = updates.quantity;
    if (updates.unit !== undefined) updateData.unit = updates.unit;
    if (updates.notes !== undefined) updateData.notes = updates.notes;
    if (updates.brandPreference !== undefined) updateData.brand_preference = updates.brandPreference;
    if (updates.categoryId !== undefined) updateData.category_id = updates.categoryId;
    if (updates.store !== undefined) updateData.store = updates.store;
    if (updates.aisle !== undefined) updateData.aisle = updates.aisle;
    if (updates.priority !== undefined) updateData.priority = updates.priority;
    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.assignedTo !== undefined) updateData.assigned_to = updates.assignedTo;
    if (updates.tags !== undefined) updateData.tags = updates.tags;
    if (updates.imageUrl !== undefined) updateData.image_url = updates.imageUrl;
    
    updateData.updated_at = new Date().toISOString();
    
    const { data, error } = await supabase
      .from('shopping_items')
      .update(updateData)
      .eq('id', itemId)
      .select(`
        *,
        category:shopping_categories(id, name, emoji, color),
        requester:family_members!requested_by(id, name, avatar),
        assignee:family_members!assigned_to(id, name, avatar)
      `)
      .single();
    
    if (error) throw error;
    return data;
  },

  async deleteItem(itemId) {
    const { error, count } = await supabase
      .from('shopping_items')
      .delete()
      .eq('id', itemId)
      .select();
    
    if (error) {
      console.error('Delete error:', error);
      throw new Error(`Failed to delete shopping item: ${error.message}`);
    }
    
    return { success: true };
  },

  async purchaseItem(itemId, purchaserId, price = null) {
    console.log('🛒 Purchasing item:', { itemId, purchaserId, price });
    
    const { data, error } = await supabase.rpc('purchase_shopping_item', {
      p_item_id: itemId,
      p_purchaser_id: purchaserId,
      p_price: price
    });
    
    if (error) {
      console.error('❌ RPC Error details:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      throw error;
    }
    
    console.log('✅ Item purchased:', data);
    
    if (data === false) {
      console.error('⚠️ RPC function returned false - check database logs for NOTICE messages');
      console.log('🔍 To debug: Check your Supabase logs or run the function manually in SQL editor');
    }
    
    return data;
  },

  // Shopping Lists operations
  async getLists(filters = {}) {
    let query = supabase
      .from('shopping_lists')
      .select(`
        *,
        creator:family_members!created_by(id, name, avatar),
        shopper:family_members!shopper_id(id, name, avatar),
        items:shopping_list_items(
          *,
          item:shopping_items(
            *,
            category:shopping_categories(id, name, emoji, color),
            requester:family_members!requested_by(id, name, avatar)
          )
        )
      `);
    
    // Apply filters
    if (filters.status) {
      query = query.eq('status', filters.status);
    }
    if (filters.type) {
      query = query.eq('type', filters.type);
    }
    
    query = query.order('created_at', { ascending: false });
    
    const { data, error } = await query;
    
    if (error) throw error;
    return data;
  },

  async createList(list, creatorId) {
    const { data, error } = await supabase
      .from('shopping_lists')
      .insert([{
        name: list.name,
        description: list.description,
        type: list.type || 'grocery',
        status: 'active',
        due_date: list.dueDate,
        budget: list.budget,
        shopping_date: list.shoppingDate,
        store: list.store,
        created_by: creatorId
      }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async updateList(listId, updates) {
    const updateData = {};
    
    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.description !== undefined) updateData.description = updates.description;
    if (updates.type !== undefined) updateData.type = updates.type;
    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.dueDate !== undefined) updateData.due_date = updates.dueDate;
    if (updates.budget !== undefined) updateData.budget = updates.budget;
    if (updates.shoppingDate !== undefined) updateData.shopping_date = updates.shoppingDate;
    if (updates.store !== undefined) updateData.store = updates.store;
    if (updates.shopperId !== undefined) updateData.shopper_id = updates.shopperId;
    
    updateData.updated_at = new Date().toISOString();
    
    const { data, error } = await supabase
      .from('shopping_lists')
      .update(updateData)
      .eq('id', listId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async deleteList(listId) {
    const { error } = await supabase
      .from('shopping_lists')
      .delete()
      .eq('id', listId);
    
    if (error) throw error;
    return { success: true };
  },

  async addItemToList(listId, itemId, addedBy, overrides = {}) {
    const { data, error } = await supabase
      .from('shopping_list_items')
      .insert([{
        list_id: listId,
        item_id: itemId,
        added_by: addedBy,
        quantity_override: overrides.quantity,
        notes_override: overrides.notes,
        priority_override: overrides.priority
      }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async removeItemFromList(listId, itemId) {
    const { error } = await supabase
      .from('shopping_list_items')
      .delete()
      .eq('list_id', listId)
      .eq('item_id', itemId);
    
    if (error) throw error;
    return { success: true };
  },

  // Favorites operations
  async getFavorites() {
    const { data, error } = await supabase
      .from('shopping_favorites')
      .select(`
        *,
        category:shopping_categories(id, name, emoji, color)
      `)
      .order('times_purchased', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  async addToFavorites(item) {
    const { data, error } = await supabase
      .from('shopping_favorites')
      .insert([{
        name: item.name,
        category_id: item.categoryId,
        default_quantity: item.defaultQuantity || 1,
        unit: item.unit,
        brand: item.brand,
        notes: item.notes
      }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Shopping History
  async getHistory(options = {}) {
    const { 
      days = 7,  // Default to 7 days instead of 30
      limit = 50, // Limit results
      offset = 0  // For pagination
    } = options;
    
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    
    const query = supabase
      .from('shopping_history')
      .select(`
        *,
        purchaser:family_members!purchased_by(id, name, avatar)
      `, { count: 'exact' })
      .gte('purchase_date', startDate.toISOString())
      .order('purchase_date', { ascending: false })
      .range(offset, offset + limit - 1);
    
    const { data, error, count } = await query;
    
    if (error) throw error;
    
    return {
      data: data || [],
      count: count || 0,
      hasMore: (count || 0) > offset + limit
    };
  },

  // Backward compatibility method
  async getFullHistory(days = 30) {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    
    const { data, error } = await supabase
      .from('shopping_history')
      .select(`
        *,
        purchaser:family_members!purchased_by(id, name, avatar)
      `)
      .gte('purchase_date', startDate.toISOString())
      .order('purchase_date', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  // Store layout operations (cached)
  getStoreLayout(storeKey) {
    // Check cache first
    const cached = getStaticDataCache();
    if (cached?.storeLayouts) {
      return cached.storeLayouts[storeKey] || cached.storeLayouts.generic;
    }
    
    // Cache static data for future use
    const staticData = {
      itemCategories: ITEM_CATEGORIES,
      storeLayouts: STORE_LAYOUTS
    };
    setStaticDataCache(staticData);
    
    return STORE_LAYOUTS[storeKey] || STORE_LAYOUTS.generic;
  },

  getAllStoreLayouts() {
    // Check cache first
    const cached = getStaticDataCache();
    if (cached?.storeLayouts) {
      return Object.entries(cached.storeLayouts).map(([key, layout]) => ({
        key,
        name: layout.name,
        layout: layout.layout
      }));
    }
    
    // Cache static data for future use
    const staticData = {
      itemCategories: ITEM_CATEGORIES,
      storeLayouts: STORE_LAYOUTS
    };
    setStaticDataCache(staticData);
    
    return Object.entries(STORE_LAYOUTS).map(([key, layout]) => ({
      key,
      name: layout.name,
      layout: layout.layout
    }));
  },

  // Sort items by store layout
  sortItemsByStoreLayout(items, storeKey) {
    const storeLayout = this.getStoreLayout(storeKey);
    const layoutMap = new Map();
    
    // Create a map of category to order
    storeLayout.layout.forEach(section => {
      layoutMap.set(section.category, section.order);
    });
    
    // Sort items by store layout order
    return items.sort((a, b) => {
      const aOrder = layoutMap.get(a.category?.name?.toLowerCase()) || 999;
      const bOrder = layoutMap.get(b.category?.name?.toLowerCase()) || 999;
      
      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }
      
      // If same category, sort by name
      return a.name.localeCompare(b.name);
    });
  },

  // Integration endpoints
  async addItemFromMessage(messageId, itemData, requesterId) {
    console.log('📧 Adding item from message:', { messageId, itemData, requesterId });
    
    const { data, error } = await supabase.rpc('add_shopping_item_from_message', {
      p_message_id: messageId,
      p_item_name: itemData.name,
      p_quantity: itemData.quantity || 1,
      p_priority: itemData.priority || 'normal',
      p_category_id: itemData.categoryId,
      p_notes: itemData.notes
    });
    
    if (error) throw error;
    
    console.log('✅ Item added from message:', data);
    return data;
  },

  async createListFromEvent(eventId, listData, creatorId) {
    console.log('📅 Creating list from event:', { eventId, listData, creatorId });
    
    // Create the list
    const list = await this.createList({
      ...listData,
      type: 'special_event'
    }, creatorId);
    
    // Link to event
    const { error } = await supabase
      .from('event_shopping_lists')
      .insert([{
        event_id: eventId,
        shopping_list_id: list.id,
        created_by: creatorId
      }]);
    
    if (error) throw error;
    
    console.log('✅ List created from event:', list);
    return list;
  },

  // Real-time subscriptions
  subscribeToItems(callback) {
    // Add unique identifier to prevent subscription conflicts
    const channelId = `shopping-items-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const subscription = supabase
      .channel(channelId)
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'shopping_items'
        }, 
        (payload) => {
          console.log('🔄 Shopping item change:', payload);
          callback(payload);
        }
      )
      .subscribe();
    
    return subscription;
  },

  subscribeToPurchases(callback) {
    // Add unique identifier to prevent subscription conflicts
    const channelId = `shopping-history-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const subscription = supabase
      .channel(channelId)
      .on('postgres_changes', 
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'shopping_history'
        }, 
        (payload) => {
          console.log('🛒 New purchase:', payload);
          callback(payload);
        }
      )
      .subscribe();
    
    return subscription;
  },

  // Unsubscribe from real-time updates
  unsubscribe(subscription) {
    if (subscription) {
      supabase.removeChannel(subscription);
    }
  },

  // Utility functions
  async estimateTotal(items) {
    // Calculate estimated total based on average prices
    // This would typically integrate with a pricing API
    const total = items.reduce((sum, item) => {
      const estimatedPrice = item.purchase_price || 0;
      return sum + (estimatedPrice * (item.quantity || 1));
    }, 0);
    
    return {
      estimatedTotal: total,
      itemCount: items.length,
      disclaimer: 'Prices are estimates based on historical data'
    };
  },

  // Search and filter
  async searchItems(searchTerm) {
    const { data, error } = await supabase
      .from('shopping_items')
      .select(`
        *,
        category:shopping_categories(id, name, emoji, color),
        requester:family_members!requested_by(id, name, avatar)
      `)
      .ilike('name', `%${searchTerm}%`)
      .order('created_at', { ascending: false })
      .limit(20);
    
    if (error) throw error;
    return data;
  },

  // Bulk operations
  async markMultipleAsPurchased(itemIds, purchaserId) {
    const results = await Promise.all(
      itemIds.map(itemId => this.purchaseItem(itemId, purchaserId))
    );
    
    return results;
  },

  async deleteMultipleItems(itemIds) {
    const { error } = await supabase
      .from('shopping_items')
      .delete()
      .in('id', itemIds);
    
    if (error) throw error;
    return { success: true, count: itemIds.length };
  }
};